import { useCallback, useEffect, useState } from "react";
import {
  ActiveProcess,
  getActiveProcesses,
} from "api/processModels";

export type DepublishWarningState = {
  open: boolean;
  loading: boolean;
  /**
   * True while the hook is fetching `/active-processes` to decide
   * whether the warning dialog is needed. Kept separate from `open`
   * so the MUI Dialog is never mounted (and never flashes) when the
   * result will end up bypassing it.
   */
  checking: boolean;
  count: number;
  names: ActiveProcess[];
};

export type UseDepublishWithWarningOptions = {
  /**
   * Called when the user confirms depublishing — the caller is
   * responsible for actually hitting the delete endpoint. This hook
   * only manages the warning dialog + flow.
   */
  onConfirm: () => void | Promise<void>;
  /**
   * Optional: called when no warning is needed (count = 0 or fetch
   * failed) so the caller can skip straight to the existing flow.
   * Defaults to `onConfirm`.
   */
  onSkip?: () => void | Promise<void>;
};

const EMPTY: DepublishWarningState = {
  open: false,
  loading: false,
  checking: false,
  count: 0,
  names: [],
};

/**
 * Manages the "this model has active processes, are you sure?" flow
 * used by both the Process page list (`ProcessModel.tsx`) and the
 * BPMN editor (`NewProcessModule.tsx`).
 *
 * Typical usage:
 *   const { warning, requestWarning, confirmWarning, cancelWarning } =
 *     useDepublishWithWarning({ onConfirm: handleDepublish });
 *
 *   <Button onClick={() => requestWarning(model.id)}>Depublish</Button>
 *   <Dialog open={warning.open} ...>...</Dialog>
 */
export const useDepublishWithWarning = ({
  onConfirm,
  onSkip,
}: UseDepublishWithWarningOptions) => {
  const [warning, setWarning] = useState<DepublishWarningState>(EMPTY);

  const close = useCallback(() => setWarning(EMPTY), []);

  // Auto-close on unmount so the dialog can't outlive the component
  // that opened it (avoids stale state across navigation).
  useEffect(() => () => close(), [close]);

  const requestWarning = useCallback(
    async (modelId: number) => {
      // Start the check WITHOUT opening the Dialog. The Dialog must
      // only be mounted once we know the warning is actually needed;
      // otherwise MUI would briefly mount the overlay and we'd see a
      // visible "flash" before the skip path closes it.
      setWarning({ ...EMPTY, checking: true });
      const data = await getActiveProcesses(modelId);
      if (!data || data.count === 0) {
        // No data (endpoint missing) or no active processes:
        // bypass the warning and proceed. `open` stays false so the
        // Dialog is never rendered.
        setWarning(EMPTY);
        await (onSkip ? onSkip() : onConfirm());
        return;
      }
      setWarning({
        open: true,
        loading: false,
        checking: false,
        count: data.count,
        names: data.activeprocesses,
      });
    },
    [onConfirm, onSkip]
  );

  const confirmWarning = useCallback(async () => {
    close();
    await onConfirm();
  }, [close, onConfirm]);

  const cancelWarning = useCallback(() => {
    close();
  }, [close]);

  return {
    warning,
    requestWarning,
    confirmWarning,
    cancelWarning,
  };
};

export default useDepublishWithWarning;
